'use client';

import Icon from '@/components/ui/AppIcon';

interface Interaction {
  medication: string;
  severity: 'mild' | 'moderate' | 'severe';
  description: string;
  recommendation: string;
}

interface InteractionWarningModalProps {
  medicationName: string;
  interactions: Interaction[];
  onClose: () => void;
}

export default function InteractionWarningModal({
  medicationName,
  interactions,
  onClose,
}: InteractionWarningModalProps) {
  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'severe':
        return 'bg-error/10 border-error/20 text-error';
      case 'moderate':
        return 'bg-warning/10 border-warning/20 text-warning';
      case 'mild':
        return 'bg-secondary/10 border-secondary/20 text-secondary';
      default:
        return 'bg-muted border-border text-foreground';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'severe':
        return 'ExclamationTriangleIcon';
      case 'moderate':
        return 'ExclamationCircleIcon';
      case 'mild':
        return 'InformationCircleIcon';
      default:
        return 'InformationCircleIcon';
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl glass rounded-xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-sage-200/30 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <Icon
                  name="ExclamationCircleIcon"
                  size={24}
                  className="text-secondary"
                  variant="solid"
                />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-semibold text-foreground mb-1">
                  Drug Interactions
                </h2>
                <p className="text-sm font-caption text-muted-foreground">
                  {medicationName} • {interactions.length} interaction
                  {interactions.length > 1 ? 's' : ''} detected
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted transition-smooth flex-shrink-0"
              aria-label="Close modal"
            >
              <Icon name="XMarkIcon" size={24} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {interactions.map((interaction, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${getSeverityStyles(
                  interaction.severity
                )}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <Icon
                    name={getSeverityIcon(interaction.severity) as any}
                    size={20}
                    className="flex-shrink-0 mt-0.5"
                    variant="solid"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-caption font-semibold">
                        {interaction.medication}
                      </h3>
                      <span className="px-2 py-0.5 rounded-full text-xs font-caption font-medium uppercase">
                        {interaction.severity}
                      </span>
                    </div>
                    <p className="text-sm font-caption opacity-90">
                      {interaction.description}
                    </p>
                  </div>
                </div>

                <div className="pl-8">
                  <p className="text-xs font-caption font-medium opacity-70 mb-1">
                    RECOMMENDATION
                  </p>
                  <p className="text-sm font-caption opacity-90">
                    {interaction.recommendation}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 rounded-lg bg-sage-50 dark:bg-sage-900/20 border border-sage-200/30">
            <div className="flex items-start gap-3">
              <Icon
                name="InformationCircleIcon"
                size={20}
                className="text-primary flex-shrink-0 mt-0.5"
              />
              <div>
                <p className="text-sm font-caption text-foreground mb-2">
                  <strong>Important:</strong> This information is provided by the
                  OpenFDA API and should not replace professional medical advice.
                </p>
                <p className="text-sm font-caption text-muted-foreground">
                  Always consult with your healthcare provider before making any
                  changes to medication regimens.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-sage-200/30 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-lg bg-primary text-primary-foreground text-base font-caption font-medium hover:bg-primary/90 transition-smooth"
          >
            Got It
          </button>
        </div>
      </div>
    </div>
  );
}